import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaChevronRight } from 'react-icons/fa';
import { Page, PageHeader, Card, SearchInput, Badge, EmptyRow, Alert, Loading } from '../../ui';
import { assignmentStatus } from '../../ui/JobParts';
import { api } from '../../ui/api';
import { getAccount } from '../../session';

// This vendor's own assignment on a job is the last one in the list the API returns.
const mine = (job) => {
  const list = job.vendor_assignment_list || [];
  return list.length ? list[list.length - 1] : null;
};

const GROUPS = [
  ['All', null],
  ['New requests', [0]],
  ['To staff', [1]],
  ['In progress', [2]],
  ['Completed', [3]],
  ['Closed', [-1, -2]],
];

/** Audits and bookings assigned to the signed-in vendor. `kind` is 'audit' or 'booking'. */
const WorkList = ({ kind, title, subtitle, idKey, detailPath, dateLabel }) => {
  const vendor = getAccount();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('All');
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api(`/${kind}/vendor/${vendor.id}`)
      .then((data) => setRows([...data].reverse()))
      .catch((e) => { setError(e.message); setRows([]); });
  }, [kind, vendor.id]);
  useEffect(() => { load(); }, [load]);

  const respond = async (e, job, accept) => {
    e.stopPropagation();
    if (!accept && !window.confirm(`Decline ${job[idKey]}? It will be offered to another vendor.`)) return;
    setBusyId(job.id);
    try {
      await api(`/${kind}/${accept ? 'accept_approval' : 'reject_approval'}/${job.id}`, { method: 'POST' });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const codeOf = (job) => (mine(job) ? mine(job).status_code : null);
  const inGroup = (job, name) => {
    const codes = GROUPS.find(([n]) => n === name)[1];
    return codes === null || codes.includes(codeOf(job));
  };
  const q = query.toLowerCase();
  const shown = (rows || []).filter((r) => inGroup(r, group) && (
    (r[idKey] || '').toLowerCase().includes(q)
    || ((r.service && r.service.service_name) || '').toLowerCase().includes(q)
    || ((r.address && r.address.city) || '').toLowerCase().includes(q)));
  const pending = (rows || []).filter((r) => codeOf(r) === 0).length;

  return (
    <Page>
      <PageHeader title={title} subtitle={subtitle} />
      <Alert>{error}</Alert>
      {pending > 0 && group !== 'New requests' && (
        <div className="alert alert-warning row" style={{ justifyContent: 'space-between' }}>
          <span><strong>{pending}</strong> new {pending === 1 ? 'request is' : 'requests are'} waiting for your response.</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setGroup('New requests')}>Review</button>
        </div>
      )}
      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by ID, service or city" />
          <div className="chips">
            {GROUPS.map(([name]) => (
              <button key={name} className={`chip${group === name ? ' on' : ''}`} onClick={() => setGroup(name)}>
                {name}{rows ? ` · ${rows.filter((r) => inGroup(r, name)).length}` : ''}
              </button>
            ))}
          </div>
        </div>
        {!rows ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr><th>ID / {dateLabel.toLowerCase()}</th><th>Service</th><th>Location</th><th>Status</th><th className="right">Action</th></tr>
              </thead>
              <tbody>
                {shown.length ? shown.map((job) => {
                  const code = codeOf(job);
                  const s = assignmentStatus(code);
                  const open = code > 0;
                  return (
                    <tr key={job.id} className={open ? 'clickable' : undefined} onClick={open ? () => navigate(`${detailPath}/${job.id}`) : undefined}>
                      <td data-label="ID" className="nowrap">
                        <span className="mono strong">{job[idKey]}</span>
                        <span className="sub">{job.date} · {(job.time || '').slice(0, 5)}</span>
                      </td>
                      <td data-label="Service" className="clip">
                        <span className="ellipsis">{job.service ? job.service.service_name : '—'}</span>
                        <span className="sub">{job.service && job.service.category ? job.service.category.name : ''}</span>
                      </td>
                      <td data-label="Location">{job.address ? `${job.address.city}, ${job.address.postal_code}` : '—'}</td>
                      <td data-label="Status"><Badge tone={s.tone}>{s.label}</Badge></td>
                      <td className="actions-cell keep">
                        {code === 0 ? (
                          <>
                            <button className="btn btn-secondary btn-sm" disabled={busyId === job.id} onClick={(e) => respond(e, job, false)}><FaTimes /> Decline</button>
                            <button className="btn btn-primary btn-sm" disabled={busyId === job.id} onClick={(e) => respond(e, job, true)}><FaCheck /> Accept</button>
                          </>
                        ) : open ? (
                          <Link to={`${detailPath}/${job.id}`} className="btn btn-secondary btn-sm" onClick={(e) => e.stopPropagation()}>
                            {code === 1 ? 'Assign staff' : 'Open'} <FaChevronRight />
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                }) : <EmptyRow cols={5}>{rows.length ? 'Nothing matches these filters.' : `No ${kind}s assigned to you yet.`}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
};

export default WorkList;
